import AppContext from 'App/AppContext';
import { IPIconButton, IPModal, IPProgressIndicator, IPSeparator, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ButtonDialogFooter from 'components/Footer/ButtonDialogFooter';
import FilePicker from 'components/Pickers/FilePicker';
import { ParentFolderInformation } from 'components/SharePoint/DirectoryExplorer';
import SharepointFilePicker from 'components/SharePoint/SharepointFilePicker';
import { allowedMimeTypesForUpload } from 'globalConstants';
import { cancelIcon, globalStackTokensGapMedium, globalTextStylesCardRed } from 'globalStyles';
import { TFunction } from 'i18next';
import mime from 'mime';
import ResourceLink from 'models/resourceLink';
import ResourceList from 'models/resourceList';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getGraphSharepointErrorMsg, isUploadCancelledError, toAppError } from 'services/Graph/graphErrors';
import { cancelGraphAsyncProces, isGraphAsyncProcesCancelled } from 'services/Graph/graphService';
import { IDriveItem } from 'services/Graph/SharepointInterfaces';
import { bytesToSize, stripFileExtension } from 'utils/string';
import {
  getClipboardFiles,
  getLibraryLinkForDriveItem,
  uploadFileToLibrary,
} from 'components/Links/Creators/clipboardImageUpload';
import { v4 as uuidv4 } from 'uuid'; // npm install uuid

interface ILinkCreatorDocumentLibraryProps {
  isOpen: boolean;
  list: ResourceList;
  //the folder in the list where the explorer opens and the upload goes, instead of the list's root
  uploadFolderId?: string;
  uploadFolderName?: string;
  dismiss: () => void;
  addLinks: (links: ResourceLink[]) => void;
  listLinks: ResourceLink[];
  existingLinks: ResourceLink[];
  listLoading: boolean;
  linkToEdit?: ResourceLink;
  hideWholeList?: boolean;
  singleSelect?: boolean;
}

// Add a type for file with ID
export type UploadFile = { id: string; file: File };

const LinkCreatorDocumentLibrary = (props: ILinkCreatorDocumentLibraryProps) => {
  const { t } = useTranslation(['library', 'translation']);
  const appContext = useContext(AppContext);
  const [selectedDriveItems, setSelectedDriveItems] = useState<IDriveItem[]>([]);
  const [selectedDriveFolder, setSelectedDriveFolder] = useState<IDriveItem | undefined>(undefined);
  const [fileTypeError, setFileTypeError] = useState<boolean>(false);
  const [linkPickError, setLinkPickError] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [currentUploadSize, setCurrentUploadSize] = useState<number>(0);
  const [currentUploadedSize, setCurrentUploadedSize] = useState<number>(0);
  const [totalUploadSize, setTotalUploadSize] = useState<number>(0);
  const [totalFullyUploadedSize, setTotalFullyUploadedSize] = useState<number>(0);
  const [numberOfFilesUploading, setNumberOfFilesUploading] = useState<number>(0);
  const [currentFileUploadingIdx, setCurrentFileUploadingIdx] = useState<number>(0);
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([]);
  const [selectedFileNames, setSelectedFileNames] = useState<Record<string, string | undefined>>({});
  const [fileNameErrors, setFileNameErrors] = useState<Record<string, string | undefined>>({});
  const [pendingFileId, setPendingFileId] = useState<string | undefined>(undefined);
  const [finishedFileIds, setFinishedFileIds] = useState<string[]>([]);

  const getLinkForDriveItem = (item: IDriveItem): ResourceLink =>
    getLibraryLinkForDriveItem(props.list, item, props.linkToEdit);

  const onUploadProgress = (totalBytes: number, sendBytes: number) => {
    setCurrentUploadSize(totalBytes);
    setCurrentUploadedSize(sendBytes);
  };

  const onSubmitWholeListItem = () => {
    const existingLink = props.listLinks.find((l) => l.isWholeList());
    let newLink: ResourceLink;
    if (existingLink) {
      newLink = existingLink;
    } else {
      newLink = new ResourceLink();
      newLink.setWholeList(props.list);
    }
    props.addLinks([newLink]);
    props.dismiss();
  };

  const getExistingLink = (links: ResourceLink[], newLink: ResourceLink): ResourceLink | undefined => {
    const linkWithId = links.find((link) => link.driveItemId === newLink.driveItemId);
    const linkWithName = links.find((link) => link.linkName.toLowerCase() === newLink.linkName.toLowerCase());

    if (linkWithId) {
      return linkWithId;
    } else if (linkWithName) {
      const linkCopies = links.filter((link) =>
        link.linkName.toLowerCase().startsWith(newLink.linkName.toLowerCase() + ' ('),
      );
      newLink.linkName += ` (${linkCopies.length + 2})`;

      return newLink;
    }

    return undefined;
  };

  //Links for files that already reached Sharepoint must always be handed to the parent, also
  //when the batch was cancelled or aborted halfway. Skipping this would leave those uploads
  //in the document library without a link pointing at them.
  const commitAndClose = (newLinks: ResourceLink[]) => {
    if (newLinks.length > 0) {
      props.addLinks(newLinks);
    }
    props.dismiss();
  };

  const onSubmit = async () => {
    if (disableAdd()) return;
    const newLinks: ResourceLink[] = [];

    try {
      // Handle uploaded files
      if (selectedFiles.length > 0) {
        setIsUploading(true);
        setNumberOfFilesUploading(selectedFiles.length);
        setTotalFullyUploadedSize(0);
        setCurrentUploadedSize(0);
        setTotalUploadSize(selectedFiles.reduce((acc, { file }) => acc + file.size, 0));
        setFinishedFileIds([]);
        setPendingFileId(undefined);
        cancelGraphAsyncProces(false);

        for (let i = 0; i < selectedFiles.length; i++) {
          const { file, id } = selectedFiles[i];
          const name = selectedFileNames[id] ?? file.name;
          setPendingFileId(id);
          setCurrentFileUploadingIdx(i);
          const newDriveItem = await fileUploadToFolderWithName(file, name, selectedDriveFolder);

          setTotalFullyUploadedSize((prev) => prev + file.size);
          setCurrentUploadedSize(0);

          if (newDriveItem) {
            const newLink = getLinkForDriveItem(newDriveItem);
            if (newLink) {
              newLinks.push(newLink);
              setFinishedFileIds((prev) => [...prev, id]);
            }
          } else {
            //the upload helper only returns undefined when the target list or the file name
            //is missing; a failed upload throws and is handled by the catch below
            appContext.showNotification(
              t('library:Picker.FileErrorUpload', { info: t('library:Picker.FileNull') }),
              true,
            );
            commitAndClose(newLinks);

            return;
          }

          //A cancel that arrives while the last chunk of this file is in flight still completes
          //that upload, so the flag has to be checked here to stop before the next file.
          if (isGraphAsyncProcesCancelled()) {
            commitAndClose(newLinks);

            return;
          }
        }
      }

      // Handle SharePoint items (files only, not folders)
      if (
        selectedDriveItems.length > 0 &&
        !selectedDriveItems.find((selectedDriveItem) => selectedDriveItem.folder !== undefined)
      ) {
        for (let idx = 0; idx < selectedDriveItems.length; idx++) {
          const item = selectedDriveItems[idx];
          const newLink = getLinkForDriveItem(item);
          if (props.linkToEdit) {
            newLinks.push(newLink);
          } else {
            const existingLink = getExistingLink(props.listLinks, newLink);
            if (existingLink) {
              newLinks.push(existingLink);
            } else {
              newLinks.push(newLink);
            }
          }
        }
      }

      commitAndClose(newLinks);
    } catch (err) {
      //a cancel is a user action, so report nothing and keep the files that did complete
      if (isUploadCancelledError(err)) {
        commitAndClose(newLinks);

        return;
      }

      const graphErrorMsg = getGraphSharepointErrorMsg(err, t);
      if (graphErrorMsg) {
        appContext.showNotification(t('library:Picker.FileErrorUpload', { info: graphErrorMsg }), true);
      } else {
        appContext.setError(toAppError(err));
      }

      //files uploaded before the failure exist in Sharepoint, so their links must not be lost.
      //when nothing was uploaded the modal stays open so the user can retry.
      if (newLinks.length > 0) {
        commitAndClose(newLinks);
      }
    } finally {
      setIsUploading(false);
      //this modal is the only place that raises the cancel, so it must also make sure a cancel
      //that was never consumed cannot leak into the next upload anywhere in the app
      cancelGraphAsyncProces(false);
    }
  };

  const onPickDriveItem = (items: IDriveItem[] | undefined) => {
    setFileTypeError(false);
    setLinkPickError(false);

    if (items?.length && items.length > 0) {
      if (items[0].folder) {
        setSelectedDriveFolder(items[0]);
      }
      setSelectedDriveItems(items);
    } else {
      setSelectedDriveItems([]);
    }
  };

  const onPickFiles = (items: File[]) => {
    // Avoid duplicates by file name
    const existingNames = new Set(selectedFiles.map((f) => f.file.name));
    const uniqueNewFiles = items.filter((f) => !existingNames.has(f.name));
    // Assign a unique ID to each new file
    const filesWithId: UploadFile[] = uniqueNewFiles.map((f) => ({ id: uuidv4(), file: f }));
    const newFiles = [...selectedFiles, ...filesWithId];

    setSelectedFiles(newFiles);
    setFileTypeError(false);
    setLinkPickError(false);

    // Set names and errors for each file by ID
    const names: Record<string, string | undefined> = { ...selectedFileNames };
    const errors: Record<string, string | undefined> = { ...fileNameErrors };
    filesWithId.forEach(({ id, file }) => {
      names[id] = file.name;
      errors[id] = validateFileName(file.name, file);
    });
    setSelectedFileNames(names);
    setFileNameErrors(errors);
  };

  const onChangeFileName = (item: File, newName: string | undefined, idx: number) => {
    const uploadFile = selectedFiles[idx];
    if (!uploadFile) return;
    const id = uploadFile.id;
    setSelectedFileNames((prev) => ({ ...prev, [id]: newName }));
    setFileNameErrors((prev) => ({ ...prev, [id]: validateFileName(newName, item) }));
  };

  const onRemoveFile = (item: File) => {
    const idx = selectedFiles.findIndex((f) => f.file === item);
    if (idx === -1) return;
    const id = selectedFiles[idx].id;
    const newFiles = selectedFiles.filter((_, i) => i !== idx);
    setSelectedFiles(newFiles);

    // Remove from name and error maps
    setSelectedFileNames((prev) => {
      const copy = { ...prev };
      delete copy[id];

      return copy;
    });
    setFileNameErrors((prev) => {
      const copy = { ...prev };
      delete copy[id];

      return copy;
    });
  };

  // Helper for uploading with name
  //a folder the user picked, else the upload folder of the host (a task's evidence folder), else the list root
  const fileUploadToFolderWithName = async (file: File, name: string, folder: IDriveItem | undefined) =>
    uploadFileToLibrary(
      appContext,
      props.list,
      file,
      name,
      t as TFunction<string[]>,
      folder?.id || !props.uploadFolderId ? folder : { id: props.uploadFolderId },
      onUploadProgress,
    );

  const disableAdd = (): boolean => {
    if (isUploading) return true;
    if (selectedFiles.length > 0) {
      if (
        fileTypeError ||
        linkPickError ||
        Object.values(fileNameErrors).some((e) => !!e) ||
        Object.keys(selectedFileNames).length !== selectedFiles.length ||
        Object.values(selectedFileNames).some((n) => !n || n.trim() === '')
      ) {
        return true;
      }

      return false;
    }

    // If picking from SharePoint, must have at least one file (not folder) selected
    if (selectedDriveItems.length === 0 || selectedDriveItems.some((item) => item.folder)) {
      return true;
    }

    // If any error
    if (fileTypeError || linkPickError) {
      return true;
    }

    return false;
  };

  const pasteHandler = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    try {
      const { files, invalid } = getClipboardFiles(event.clipboardData.items);
      if (files.length > 0) {
        onPickFiles(files);
        event.preventDefault();
      } else if (invalid) {
        setFileTypeError(true);
      }
    } catch {
      setSelectedFiles([]);
      setSelectedFileNames({});
      setFileNameErrors({});
      onPickFiles([]);
    }
  };

  const validateFileName = (newName: string | undefined, file: File) => {
    if (!newName || newName.trim() === '') {
      return t('library:Picker.FileNameErrorEmpty');
    } else {
      //invalid chars
      const invalidSharepointChars = '"*:<>?/\\|#%';
      for (let i = 0; i < newName.length; i++) {
        if (invalidSharepointChars.indexOf(newName.charAt(i)) !== -1) {
          return t('library:Picker.FileNameErrorInvalid', { chars: invalidSharepointChars });
        }
      }
      const newNameLower = newName.toLowerCase();
      //file name cannot contain '_vti_'
      const invalidVTI = '_vti_';
      if (newNameLower.indexOf(invalidVTI) >= 0) {
        return t('library:Picker.FileNameErrorInvalid', { chars: invalidVTI });
      }
      //file name cannot start with '~$'
      const invalidStart1 = '~$';
      if (newNameLower.startsWith(invalidStart1)) {
        return t('library:Picker.FileNameErrorInvalid', { chars: invalidStart1 });
      }
      //file name cannot be:
      const invalidNames: string[] = [
        'desktop.ini',
        '.lock',
        'con',
        'prn',
        'aux',
        'nul',
        'com0',
        'com1',
        'com2',
        'com3',
        'com4',
        'com5',
        'com6',
        'com7',
        'com8',
        'com9',
        'lpt0',
        'lpt1',
        'lpt2',
        'lpt3',
        'lpt4',
        'lpt5',
        'lpt6',
        'lpt7',
        'lpt8',
        'lpt9',
      ];
      const nameWithoutExt = stripFileExtension(newNameLower);
      if (invalidNames.includes(nameWithoutExt)) {
        return t('library:Picker.FileNameErrorInvalid', { chars: invalidNames.join(', ') });
      }

      const allowedMimeTypes = Object.keys(allowedMimeTypesForUpload);
      const mimeType = file.type;
      const mimeTypeFromName = mime.getType(newNameLower) || '';
      const allExtensions = mime.getAllExtensions(mimeType);

      const mimeTypeMatch = mimeTypeMatchesAllowed(mimeTypeFromName, allowedMimeTypes);

      if (!mimeTypeMatch && allExtensions) {
        // Check both the file's MIME type and the guessed MIME type from the name
        return t('library:Picker.FileNameErrorInvalidExtension', {
          extensions: [...allExtensions].map((ext) => `.${ext}`).join(' '),
        });
      }
    }

    return undefined;
  };

  const onCancel = () => {
    if (isUploading) {
      cancelGraphAsyncProces(true);
    }
    props.dismiss();
  };

  const getParentInfo = (): ParentFolderInformation | undefined => {
    if (props.list.spDriveId && props.list.spDriveItemId) {
      const parentFolderInfo: ParentFolderInformation = {
        driveId: props.list.spDriveId,
        driveItemId: props.uploadFolderId ?? props.list.spDriveItemId,
        tenantId: props.list.altTenantId,
      };

      return parentFolderInfo;
    }

    return undefined;
  };

  const currentFile = currentFileUploadingIdx + 1;
  const totalFiles = numberOfFilesUploading;
  const currentUploadSendSize = bytesToSize(totalFullyUploadedSize + currentUploadedSize);
  const totalUploadSizeBytes = bytesToSize(totalUploadSize);
  const percentage = Math.round(((currentUploadedSize + totalFullyUploadedSize) / totalUploadSize) * 100);

  let progressLabel = '';
  progressLabel +=
    totalFiles > 1 ? ` ${t('library:Picker.UploadingFiles', { current: currentFile, total: totalFiles })}, ` : '';
  progressLabel += `${t('library:Picker.UploadingBytes', {
    uploadedSize: currentUploadSendSize,
    totalSize: totalUploadSizeBytes,
  })}, `;
  progressLabel += t('library:Picker.UploadingPercentage', { percentage });

  return (
    <IPModal isBlocking isOpen={props.isOpen} onDismiss={onCancel}>
      <IPStack
        verticalFill
        styles={{ root: { height: '90vh', width: 1000, minWidth: 320, maxWidth: '90vw', padding: 20 } }}
        onPaste={pasteHandler}
      >
        {/* Header */}
        <IPStack.Item>
          <IPStack horizontal horizontalAlign={'space-between'}>
            <IPStack.Item>
              {!props.linkToEdit && (
                <IPText variant="xLarge">
                  {t('library:LinkComponents.AddItemOf', {
                    name: props.uploadFolderName ? `${props.list.name} / ${props.uploadFolderName}` : props.list.name,
                  })}
                </IPText>
              )}
              {props.linkToEdit && (
                <IPText variant="xLarge">
                  {t('library:LinkComponents.ReplaceItem', { name: props.linkToEdit.linkName, list: props.list.name })}
                </IPText>
              )}
            </IPStack.Item>
            <IPStack.Item>
              <IPIconButton iconProps={cancelIcon} onClick={onCancel} />
            </IPStack.Item>
          </IPStack>
          <IPSeparator />
        </IPStack.Item>
        {/* Body */}
        <IPStack.Item grow>
          <IPStack verticalFill tokens={globalStackTokensGapMedium}>
            <IPStack.Item grow>
              <SharepointFilePicker
                onSelect={onPickDriveItem}
                invalidAuthToken={props.dismiss}
                parentFolder={getParentInfo()}
                existingLinks={props.existingLinks}
                linkToEdit={props.linkToEdit}
                singleSelect={props.singleSelect}
                disabled={isUploading}
              />
            </IPStack.Item>
            <IPStack.Item>
              <FilePicker
                selectedFiles={selectedFiles}
                selectedFileNames={selectedFileNames}
                onSelect={onPickFiles}
                onChangeName={onChangeFileName}
                onRemove={onRemoveFile}
                fileNameErrors={fileNameErrors}
                onFileTypeError={() => setFileTypeError(true)}
                actionPending={isUploading}
                pendingFileId={pendingFileId}
                finishedFileIds={finishedFileIds}
              />
            </IPStack.Item>
            {fileTypeError && (
              <IPStack.Item>
                <IPText styles={globalTextStylesCardRed}>{t('library:LinkComponents.FileTypeError')}</IPText>
              </IPStack.Item>
            )}
            {linkPickError && (
              <IPStack.Item>
                <IPText styles={globalTextStylesCardRed}>{t('library:LinkComponents.LinkPickError')}</IPText>
              </IPStack.Item>
            )}
            {isUploading && currentUploadSize > 0 && (
              <IPStack.Item>
                <IPProgressIndicator
                  label={progressLabel}
                  barHeight={5}
                  percentComplete={(currentUploadedSize + totalFullyUploadedSize) / totalUploadSize}
                />
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
        {/*Footer */}
        <IPStack.Item>
          <ButtonDialogFooter
            primaryText={props.linkToEdit ? t('translation:General.Button.Save') : t('translation:General.Button.Add')}
            secondaryText={t('translation:General.Button.Cancel')}
            onSubmit={() => {
              onSubmit();
            }}
            onCancel={onCancel}
            buttonPosition="end"
            disablePrimary={disableAdd}
            tertiaryText={props.hideWholeList ? undefined : t('library:LinkComponents.WholeList')}
            tertiaryTooltip={
              props.existingLinks.find((l) => l.isWholeList()) !== undefined
                ? t('library:LinkComponents.Validate.ItemExists')
                : undefined
            }
            disableTertiary={() => props.existingLinks.find((l) => l.isWholeList()) !== undefined}
            onTertiary={onSubmitWholeListItem}
          />
        </IPStack.Item>
      </IPStack>
    </IPModal>
  );
};

function mimeTypeMatchesAllowed(mimeType: string, allowedMimeTypes: string[]): boolean {
  return allowedMimeTypes.some((allowed) => {
    if (allowed.endsWith('/*')) {
      // e.g. allowed = 'text/*', mimeType = 'text/plain'
      return mimeType.startsWith(allowed.slice(0, allowed.length - 1));
    }

    return allowed === mimeType;
  });
}

export default LinkCreatorDocumentLibrary;